import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Trash2 } from 'lucide-react';
import Modal from '../Modal';
import { useConfirm } from '../ConfirmDialog';
import { teamsApi } from '../../api/teams';
import { companiesApi } from '../../api/companies';
import { useCompany } from '../../contexts/CompanyContext';
import type { Team } from '../../types';

interface TeamEditorModalProps {
  /** null = create a new team */
  team: Team | null;
  onClose: () => void;
}

/**
 * Create/edit a team: name, description and membership. Membership is a
 * checkbox list of the company's staff — a person can belong to any number
 * of teams, so nothing is exclusive here.
 */
export default function TeamEditorModal({ team, onClose }: TeamEditorModalProps) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { currentCompany } = useCompany();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const teamId = (team as any)?.id ?? (team as any)?._id;

  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [memberIds, setMemberIds] = useState<string[]>(
    (team?.members ?? [])
      .map((m: any) => m.userId ?? m.user?.id)
      .filter(Boolean) as string[],
  );

  const { data: members = [] } = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => companiesApi.getMembers(companyId),
    enabled: !!companyId,
  });
  const staff = (members as any[]).filter((m: any) =>
    ['technician', 'admin', 'owner'].includes(m.role),
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        memberIds,
      };
      return teamId ? teamsApi.update(teamId, payload) : teamsApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success(teamId ? 'Team updated' : 'Team created');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => teamsApi.delete(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Team deleted');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete team?',
      message: `"${team?.name}" will no longer be assignable. Tickets already assigned to it keep their assignment.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) deleteMutation.mutate();
  };

  const toggleMember = (id: string) =>
    setMemberIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const busy = saveMutation.isPending || deleteMutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={teamId ? 'Edit Team' : 'New Team'}
      busy={busy}
      footer={
        <>
          {teamId && (
            <button
              type="button"
              className="btn btn-danger mr-auto inline-flex items-center gap-1.5"
              onClick={handleDelete}
              disabled={busy}
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => saveMutation.mutate()}
            disabled={busy || !name.trim()}
          >
            {saveMutation.isPending ? 'Saving…' : teamId ? 'Save changes' : 'Create team'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label">Team name</label>
          <input
            className="input w-full"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Support Tier 1"
            autoFocus
          />
        </div>
        <div>
          <label className="label">Description</label>
          <input
            className="input w-full"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div>
          <label className="label">Members</label>
          <p className="text-xs text-muted mb-2">
            Everyone on the team is notified when a ticket is assigned to it.
            A person can belong to more than one team.
          </p>
          <div className="max-h-56 overflow-y-auto rounded-lg border border-line divide-y divide-line-light">
            {staff.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted">No staff members found.</p>
            ) : (
              staff.map((m: any) => {
                const uid = m.user?.id;
                const label = m.user?.name ?? m.user?.email ?? uid;
                return (
                  <label
                    key={uid}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-row-odd"
                  >
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={memberIds.includes(uid)}
                      onChange={() => toggleMember(uid)}
                    />
                    <span className="text-fg">{label}</span>
                    <span className="ml-auto text-xs text-muted capitalize">{m.role}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
